import { useAutoSave } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { localized } from "@msuite/ui/localization";
import { DataTable, useDataTableContext } from "@msuite/ui/table";
import { MeasurementStatementTag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import {
	type MeasurementStatementFormSchema,
	measurementStatementFormSchema,
} from "@msuite/utils/schemas";
import type { ProjectMeasurement } from "@msuite/utils/types";
import { useForm } from "react-hook-form";
import { client } from "@/hono";
import { useMeasurementContext } from "./context";

/** Props Interface */
interface MeasurementStatementSelectProps {
	row: ProjectMeasurement;
}

export function MeasurementStatementSelect({ row }: MeasurementStatementSelectProps) {
	/** Hooks */
	const tableContext = useDataTableContext();
	const measurementContext = useMeasurementContext();

	/** Render */
	const methods = useForm({
		resolver: resolver(measurementStatementFormSchema),
		defaultValues: row.statement
			? { statement: row.statement as unknown as MeasurementStatementFormSchema["statement"] }
			: undefined,
	});

	useAutoSave(methods, handleOnChange, 0);

	if (measurementContext.type === "controlling") {
		return <MeasurementStatementTag statement={row.statement} />;
	}

	/** Functions */
	async function handleOnChange(json: MeasurementStatementFormSchema) {
		const batchIds = tableContext.table.getSelectedRowModel().rows.map((row) => row.id);
		const response = await client.measurements[":projectId"].measurements[
			":ticketAssignmentId"
		].statement.$post({
			param: {
				projectId: row.projectId,
				ticketAssignmentId: row.id,
			},
			json: {
				...json,
				batchIds,
			},
		});
		await responseHandler(response);
		await tableContext.refetch?.();
		if (batchIds.length > 1) {
			tableContext.handleOnSetItemRenderId();
			tableContext.table.resetRowSelection();
		}
	}

	/** Render */
	return (
		<DataTable.Select
			isDisabled={row.isClosed}
			methods={methods}
			name="statement"
			renderOption={(_, __, value) => {
				return <MeasurementStatementTag statement={value} />;
			}}
			options={
				[
					{
						value: "remember",
						label: localized("measurements.options.remember"),
						item: {
							statement: "remember",
						},
					},
					{
						value: "without-charge",
						label: localized("measurements.options.without_charge"),
						item: {
							statement: "without-charge",
						},
					},
					{
						value: "included",
						label: localized("measurements.options.included"),
						item: {
							statement: "included",
						},
					},
				] as const
			}
		/>
	);
}
